import * as apiConstants from './src/api/api.constants';
import * as apiFault from './src/api/api.fault.helpers';
import * as apiState from './src/api/api.state';
import * as thunkHelpers from './src/api/thunk.helper';
import * as applicationHelpers from './src/application/application.helpers';
import * as applicationState from './src/application/application.state';
import * as appEnvironmentHelpers from './src/appSettings/app.environment';
import * as appSettingsHelpers from './src/appSettings/app.settings.helpers';
import * as appSettingsState from './src/appSettings/app.settings.state';
import * as codeTableConstants from './src/codeTables/codeTables.constants';
import * as codeTableState from './src/codeTables/codeTables.state';
import * as codeTableValues from './src/codeTables/codeTables.values';
import * as customerCareTemplateConfig from './src/components/configurable/customercare';
import { DEFAULT_DBSS_TEMPLATE } from './src/components/configurable/dbssDefaultTemplate';
import { DEFAULT_ACCOUNT_STATUS } from './src/components/configurable/defaultAccountStatus';
import { DEFAULT_OTT_TEMPLATE } from './src/components/configurable/ottDefaultTemplate';
import * as detailViewTemplateConfig from './src/components/configurable/detailViewTemplates';
import * as briConstants from './src/constants/bris';
import * as commonConstants from './src/constants/common';
import * as entitySpecConstants from './src/constants/entitySpec';
import * as pagingConstants from './src/constants/paging';
import * as unitConstants from './src/constants/unitOfMeasure';
import * as guidanceCodeState from './src/guidanceCode/guidanceCode.state';
import * as arrayHelpers from './src/helpers/array.helpers';
import * as dateHelpers from './src/helpers/date.helpers';
import * as downloadHelpers from './src/helpers/download.helpers';
import * as formHelpers from './src/helpers/form.helpers';
import {
  CodeTableAdditionalPropertyTypeOf as CodeTableAdditionalPropertyTypeOfType,
  CodeTableCodeTypeOf as CodeTableCodeTypeofType,
  CodeTableNumber,
  InternalCodeTableNumber,
  MetadataCodeRecordWithAdditionalPropertiesDictionary as MetadataCodeRecordWithAdditionalPropertiesDictionaryType,
  MetadataCodeTypeOf as MetadataCodeTypeOfType,
  MetadataCodeWithAdditionalPropertiesDictionary as MetadataCodeWithAdditionalPropertiesDictionaryType
} from './src/interfaces/codeTables';
import * as languageState from './src/language/language.state';
import * as metadataQueries from './src/metadata/apis';
import * as passwordState from './src/password/password.state';
import * as loadingState from './src/redux/loading.state';
import * as permissionService from './src/security/permission.service';
import * as sessionState from './src/security/session.state';
import * as sessionHelpers from './src/session/session.helpers';
import * as localStorage from './src/utility/local.storage';
import * as sessionStorage from './src/utility/session.storage';
import * as sessionStorageKeys from './src/utility/session.storage.keys';

export * from './src/shared/interfaces/FileManagement';

export { retrieveAmazonAwsData } from './src/api/amazonaws.api';
export {
  axiosSuccessResponseInterceptor,
  axiosRejectedResponseInterceptor
} from './src/api/api.error.helpers';
export type {
  ApiFaultOrWarnings,
  ApiFault as ApiFaultType,
  ApiWarning
} from './src/api/api.fault.interfaces';
export { ConfigureServices } from './src/api/api.helpers';
export type {
  ApiDispatchActionCreator,
  ApiDispatchOptions,
  ApiResource,
  CreateMicroserviceResourceOptions
} from './src/api/api.interfaces';
export { setIsApiConfigured } from './src/api/api.state';
export { retrieveMetadata } from './src/api/metadata.api';
export {
  setGlobalMetadataLanguageOverride,
  getGlobalMetadataLanguageOverride,
  useGlobalMetadataLanguageOverride
} from './src/api/metadata.api.helpers';
export { getAxiosMicroserviceInstance } from './src/api/microservice.factory';
export { createMicroserviceResource } from './src/api/microserviceResource.factory';
export {
  getAppointmentsService,
  ReactQueryClientProvider,
  useCategoriesMetadata,
  useCategoriesMetadataDictionary,
  useCodeTableBusinessUnitOrGlobalConfig,
  useCodeTableMetadata,
  useCodeTableMetadataDictionary,
  useCodeTableMetadataLabelValuePair,
  UseMutationOptions,
  useOfferingMetadata,
  useOfferingPricingPlanMetadataDictionary,
  useOfferingProductMetadataDictionary,
  useOfferingsMetadata,
  useOfferingsPricingPlansMetadataDictionary,
  useOfferingsProductsMetadataDictionary,
  useProductMetadata,
  useProductPricingPlanMetadataDictionary,
  useProductsMetadata,
  useReactMutation,
  useReactQueries,
  useReactQuery,
  useReactQueryClient,
  useInventoryTypeMetadata,
  useInventoryTypAttributesMetadataDictionary,
  useInventoryTypSKUsMetadataDictionary,
  useInventorySkuDetailsMetadata,
  InventorySkuDetailsMetadata,
  useInventorySkuImagesMetadataDictionary
} from './src/api/reactQuery';
export type { InventorySkuDetailsMetadataParams } from './src/api/reactQuery';
export type { Metadata_Offering_Overridden } from './src/api/reactQuery';
export {
  retrieveData as getGetServicePromise,
  sendDataDeprecated as getPostServicePromiseDeprecated,
  sendData as getPostServicePromise,
  getServicesRequestInstance
} from './src/api/services.api';
export type { CreateThunkThunkApi } from './src/api/thunk.helper.interface';
export { Application } from './src/application/application';
export { ApplicationContext } from './src/application/application.context';
export type { ApplicationContextProps } from './src/application/application.context.interfaces';
export { useBootstrapApplication } from './src/application/application.hooks';
export type { AngularRoutes } from './src/application/application.interfaces';
export { useAngularModuleRoute } from './src/application/useAngularModuleRoute';
export {
  isDevelopmentEnvironment,
  isServerMimicMode,
  getServerMimicBasePath
} from './src/appSettings/app.environment';
export { isQAEnvironment } from './src/appSettings/app.settings.helpers';
export { isPublicRoute } from './src/publicRoutes/isPublicRoute';
export {
  PUBLIC_ROUTE_PARAM,
  readPublicRouteContext
} from './src/publicRoutes/publicRouteContext';
export type { PublicRouteContext } from './src/publicRoutes/publicRouteContext';
export { useAnonymousApiConfiguration } from './src/publicRoutes/useAnonymousApiConfiguration';
export type {
  AnonymousApiConfig,
  AnonymousApiConfigResult
} from './src/publicRoutes/useAnonymousApiConfiguration';
export type {
  AnonymousApiError,
  PublicRouteDefinition
} from './src/publicRoutes/publicRoutes.types';

export type { AppointmentType } from './src/api/reactQuery/appointments';
export {
  useRetrieveAppointmentTypesQuery,
  AppointmentTypeStatus
} from './src/api/reactQuery/appointments';

export type {
  AccountStatusWidget,
  AccountStatusWidgets,
  ActivitiesSubNav,
  BillingSubNav,
  CustomerCareTemplate,
  CustomerCareTemplateSubNavigationDetails,
  CustomercareTemplateSubNavigations,
  CustomPage,
  GlobalActions,
  Pages,
  QuickActions,
  SystemWidgets
} from './src/components/configurable/customerCare.interfaces';
export { CustomPageStatus } from './src/components/configurable/customerCare.interfaces';
export { BillerRuleInstanceType } from './src/constants/bris';
export {
  Format as EntitySpecPropertyFormat,
  Type as EntitySpecPropertyType
} from './src/constants/entitySpec';
export type {
  CapturedApiError,
  CapturedApiFaultError,
  CapturedError,
  ServerErrorHandlerType
} from './src/errorHandling/errorHandling.types';
export { ErrorHandler } from './src/errorHandling/ErrorHandler';
export { ErrorConverter } from './src/errorHandling/ErrorConverter';
export { ErrorLogger } from './src/errorHandling/ErrorLogger';
export { ErrorSanitizer } from './src/errorHandling/ErrorSanitizer';
export { ERROR_SCOPE_ORDER_FLOW } from './src/errorHandling/errorHandling.config';
export { useSubmitErrorLog } from './src/errorHandling/useSubmitErrorLog';
export type {
  ErrorHandlerConfig,
  ErrorHandlingOptions,
  ErrorHandlingResult,
  ErrorMiddleware,
  RuntimeErrorContext
} from './src/errorHandling/errorHandling.types';
export { useErrorHandling } from './src/errorHandling/errorHandling.hook';
export {
  FeatureToggleType,
  UIFeatureToggle,
  UICustomizationFeatureToggle,
  UICustomizationValue,
  CustomizationValueDataType,
  PhysicalInventoryAvailabilityDisplayMode
} from './src/featureToggle/featureToggle.constants';
export type {
  FeatureToggleDataType,
  CustomizationValueConfig
} from './src/featureToggle/featureToggle.constants';
export {
  FeatureCategory,
  FeatureToggleGroupType,
  getUIFeatureToggleConfigFields
} from './src/featureToggle/featureToggle.catalog';
export type {
  FeatureToggleCatalogGroup,
  FeatureToggleCatalogItem
} from './src/featureToggle/featureToggle.catalog';
export {
  areFeatureOverridesEnabled,
  filterCustomizationsByCatalog,
  filterEligibleFeatureToggleOverrides,
  filterFeatureTogglesByCatalog,
  getActiveOverrideCount,
  getOverridableFeatureToggleGroups,
  getOverridableFeatureToggleKeys,
  isFeatureOverridesPanelAvailable,
  OverrideValue,
  toOverrideValue,
  toPersistedOverrides
} from './src/featureToggle/featureOverrides.helpers';
export type {
  OverridableFeatureToggle,
  OverridableFeatureToggleGroup,
  OverridableFeatureToggleKey
} from './src/featureToggle/featureOverrides.helpers';
export {
  FeatureToggleContext,
  FeatureToggleProvider,
  useFeatureToggleContext,
  useFeatureOverridesAccess
} from './src/featureToggle/useFeatureToggleContext';
export { REGEXES } from './src/formValidation/validation.constants';
export {
  groupObjectArrayItemsByKey,
  pluck,
  transformKeyValueArrayToObject,
  transformObjectArrayToObject
} from './src/helpers/array.helpers';
export {
  getChildCountWithoutFragments,
  hasValidChildren
} from './src/helpers/common.helpers';
export { applyDefaultValuesWhenEmpty } from './src/helpers/additionalProperty.helpers';
export {
  formatWithPattern,
  formatWithRegularExpressionCodes
} from './src/helpers/formatPattern.helpers';
export type { RegularExpressionLookup } from './src/helpers/formatPattern.helpers';
export type { AdditionalPropertyMetadataDictionary } from './src/helpers/additionalProperty.helpers';
export { DownloadContentType } from './src/helpers/download.helpers';
export { getValidFormValues } from './src/helpers/form.helpers';
export { getCreateSubscriberRequirements } from './src/helpers/customer.requirements.helpers';
export { isInIframeOrWebView, isWebView } from './src/helpers/message.helpers';
export { toNumber } from './src/helpers/string.helpers';
export { useApiDispatch } from './src/hooks/dispatch.hook';
export { useBusinessUnitLanguageOptions } from './src/hooks/useBusinessUnitLanguageOptions';
export { useMatchAndFormatPatterns } from './src/hooks/useMatchAndFormatPatterns';
export { useNotification } from './src/hooks/notify.hook';
export { useCustomEvent } from './src/hooks/useCustomEvent';
export { useEffectExceptOnMount } from './src/hooks/useEffectExceptOnMount.hook';
export { useHistoryPersistState } from './src/hooks/useHistoryPersistState.hook';
export { useIsDbss } from './src/hooks/useIsDbss';
export {
  MIN_SUPPORTED_SCREEN_WIDTH_IN_PX,
  useMediaQuery
} from './src/hooks/useMediaQuery';
export {
  DeviceCapabilitiesProvider,
  useDeviceCapabilities,
  DEVICE_BREAKPOINTS
} from './src/hooks/useDeviceCapabilities';
export type {
  DeviceCapabilities,
  ScreenSize,
  PointerType,
  Orientation
} from './src/hooks/useDeviceCapabilities';
export { useTranslation } from './src/hooks/useTranslation.hook';
export type { TFunction } from './src/hooks/useTranslation.hook';
export {
  TranslationProvider,
  useTranslationContext
} from './src/hooks/useTranslationContext.hook';
export { initI18n, translate } from './src/i18n';
export {
  InteractionCategory,
  InteractionContextKey
} from './src/interactions/interactions.constants';
export {
  SortByType,
  SortDirection
} from './src/interactions/interactions.interfaces';
export type {
  Interaction,
  InteractionThumbnail
} from './src/interactions/interactions.interfaces';
export {
  clearSearchCriteria as clearInteractionSearchCriteria,
  InteractionSearchCriteriaSelector,
  InteractionSelector,
  InteractionTypeOptionsSelector
} from './src/interactions/interactions.state';
export { useInteraction } from './src/interactions/useInteraction.hook';
export { useInteractions } from './src/interactions/useInteractions.hook';
export { useIsPIIDataAccessEnabled } from './src/interactions/useIsPIIDataAccessEnabled';
export {
  AdditionalPropertyEntityType,
  AdditionalPropertyInputType,
  CodeTableNumber as CodeTables,
  InternalCodeTableNumber as CodeTablesInternal,
  DurationPeriodType,
  EntryShell
} from './src/interfaces';
export type {
  AdditionalProperty,
  BillerRuleInstanceCharge,
  BusinessUnit,
  Cardinality,
  CodeTableNumber,
  CoreState,
  CustomBillerRuleInstance,
  CustomerCareTemplateConfiguration,
  CustomTile,
  CustomTileType,
  CustomerCareTemplateItem,
  EarlyTerminationFeeBillerRuleInstance,
  EntitlementBillerRuleInstance,
  EntityFormFieldProps,
  EntitySpecExtension,
  EntitySpecFilter,
  EntitySpecOverride,
  EntitySpecResponse,
  EntitySpecSchema,
  FinanceBillerRuleInstance,
  Group,
  GroupTemplate,
  Identifier,
  InternalCodeTableNumber,
  InvAction,
  InvApplication,
  InvFieldInputProps,
  KeyValueOf,
  KeyValuePair,
  LabelValuePair,
  OfferingOption,
  OneTimeBillerRuleInstance,
  Optional,
  PricingPlan,
  PricingPlanBillerRuleInstancesMetadata,
  PricingPlanMetadata,
  PricingPlanThumbnail,
  Product,
  ProductContextBillerRuleInstance,
  ProductMetadata,
  ProductPricingPlanLifeCycle,
  ProductPricingPlanLifeCycleStep,
  ProductThumbnail,
  RecurringBillerRuleInstance,
  RetrieveSessionResponse,
  SchemaDefinition,
  SchemaDefinitionExtension,
  SchemaDefinitionObjects,
  SecurityAttributeSetting,
  SessionSummary,
  TriggerBillerRuleInstance,
  UsageBillerRuleInstance,
  UsageRule
} from './src/interfaces';
export {
  CatalogClassification,
  CatalogOfferingStatuses,
  CatalogPricingPlanStatuses,
  CatalogProductStatuses,
  CatalogStructureType
} from './src/interfaces/catalog';
export type {
  CatalogOfferingThumbnail,
  CatalogPricingPlanThumbnail,
  CatalogProductThumbnail
} from './src/interfaces/catalog';
export {
  OrderChangeTypeCode,
  OrderTypeCode
} from './src/interfaces/codeTables/reasonCodes';
export type { CoreStateBase } from './src/interfaces/coreState';
export type { Requirements } from './src/interfaces/customer';
export type { OfferingThumbnail } from './src/interfaces/offering';
export type {
  OrderPaymentInstrumentTagTypes,
  OrderOfferingPaymentInstrumentTagTypes,
  TermsAndConditionsTagTypes
} from './src/interfaces/termsAndConditions';
export {
  TermsAndConditionsEntityType,
  TermsAndConditionsEntitySubType
} from './src/interfaces/termsAndConditions';
export {
  fetchProductMetadataThunk,
  getAllProductMetadata,
  getCaseActivityMapByCaseTypeMetadata,
  getDiscountsMetadata,
  getMediaMetadata,
  getOfferingMetadata,
  getOfferingPricingPlansMetadata,
  getOfferingProductsMetadata,
  getPricingPlanUsageRuleIds,
  getPricingPlanUsageRules,
  //getPricingPlansMetadata,
  getProductMetadata,
  getProductPricingPlanMapMetadata,
  getSubtenantsMetadata,
  getSubtenantsMetadataDictionary,
  getUsageRulesMetadata,
  loadCaseTypeActivitiesMetadata,
  loadDiscountsMetadata,
  loadMediaMetadata,
  loadMetadata,
  loadOfferingMetadata,
  loadProductMetadata,
  loadUsageRulesForPricingPlanMetadata
} from './src/metadata/metadata.state';
export { default as reducer } from './src/redux/root.reducer';
export { default as SessionStorageMiddleware } from './src/redux/session.storage.middleware';
export { sendToLogin } from './src/security/auth.helpers';
export { useUpdatePreferencesMutation } from './src/security/useUpdatePreferencesMutation';
export {
  SessionTermination,
  pruneStaleSessionActivity,
  sessionActivityKey,
  useSessionWatch
} from './src/session/termination';
export { PermissionType } from './src/security/permission.interfaces';
export type { SecurityAttribute } from './src/security/permission.interfaces';
export {
  BusinessUnitTimeZoneSelector,
  UserFeatureToggleOverridesSelector
} from './src/security/session.state';
export { isInternalCodeTable } from './src/utility/codeTables';
export { clone, isEmpty, merge } from './src/utility/common';
export {
  buildAuthLogoutUrl,
  getCurrentRelativeUrl,
  getReactAuthBasePath
} from './src/utility/location';
export type {
  AuthLogoutUrlOptions,
  LogoutReason
} from './src/utility/location';
export {
  createNewDataFromSchema,
  extendEntitySpec
} from './src/utility/entitySpec';
export { openTab } from './src/utility/newWindow';
export {
  convertUnitsUserFriendly,
  convertUnitsUserFriendlyIntoSmallerDescriptiveUnits,
  getAllUnitsInTheUnitCategory
} from './src/utility/unitConversion/convert';
export { buildYupSchemaFromEntitySpec, configureYup } from './src/utility/yup';
export type {
  EntityIdValidation,
  ExternalReferencesValidation,
  KeyValuePairValidation,
  LabelValuePairValidation,
  OneOfEnumValidation,
  PercentageValidation,
  TelephoneNumberValidation,
  UniqueArrayValidation,
  UniqueArrayWithMultipleKeysValidation
} from './src/utility/yup.validations.interfaces';
export { StorageService } from './src/utility/StorageService';
export {
  DebugManager,
  type DebugManagerConfig,
  type DebugLogCategory,
  type DebugLogEntry
} from './src/utility/DebugManager';

export type { CustomWidget } from './src/interfaces/templateConfiguration';
export {
  CustomWidgetLocation,
  CustomWidgetOrderStep,
  CustomWidgetOrderStepToKey,
  CustomWidgetOrderType,
  IntegrationType as CustomWidgetIntegrationType
} from './src/interfaces/templateConfiguration';
export { TIMEZONE_OFFSET_TO_IANA } from './src/constants/common';
export {
  parseBooleanConfig,
  useBusinessUnitTimeZoneOffsetToIANA
} from './src/helpers/common.helpers';
export type {
  DetailViewTemplatePageId,
  DetailViewTemplateConfiguration,
  DetailViewTemplateDefinition,
  DetailViewTemplatePageDefinitions,
  DetailViewTemplateConfigurationItem,
  DetailViewTemplateColumnDefinition
} from './src/components/configurable/detailViewTemplates';
export {
  getDetailViewTemplatePageDefinitions,
  getDetailViewTemplatePageDisplayName
} from './src/components/configurable/detailViewTemplates';

export const ApiConstants = apiConstants;
export const ApiState = apiState;
export const ApiFault = apiFault;
export const ApplicationHelpers = applicationHelpers;
export const ApplicationState = applicationState;
export const AppSettingsHelpers = appSettingsHelpers;
export const AppEnvironmentHelpers = appEnvironmentHelpers;
export const AppSettingsState = appSettingsState;
export const ArrayHelpers = arrayHelpers;
export const DownloadHelpers = downloadHelpers;
export const BriConstants = briConstants;
export const CodeTablesConstants = codeTableConstants;
export const CodeTableValues = codeTableValues;
export const CodeTableState = codeTableState;
export const CommonConstants = commonConstants;
export const CustomerCareTemplateConfig = customerCareTemplateConfig;
export const DetailViewTemplateConfig = detailViewTemplateConfig;
export const CustomerCareTemplateConstants = {
  DEFAULT_ACCOUNT_STATUS,
  DEFAULT_DBSS_TEMPLATE,
  DEFAULT_OTT_TEMPLATE
};
export const DateHelpers = dateHelpers;
export const FormHelpers = formHelpers;
export const EntitySpecConstants = entitySpecConstants;
export const LanguageState = languageState;
export const GuidanceCodeState = guidanceCodeState;
export const LoadingState = loadingState;
export const PagingConstants = pagingConstants;
export const PasswordState = passwordState;
export const PermissionService = permissionService;
export const SessionState = sessionState;
export const SessionStorage = sessionStorage;
export const LocalStorage = localStorage;
export const SessionStorageKeys = sessionStorageKeys;
export const SessionHelpers = sessionHelpers;
export {
  CustomerCareColorScheme,
  isCustomerCareColorScheme
} from './src/session/customerCareColorScheme';
export type { CustomerCareColorSchemeMode } from './src/session/customerCareColorScheme';
export const ThunkHelpers = thunkHelpers;
export const MetadataQueries = metadataQueries;
export const UnitConstants = unitConstants;

export type CodeTableAdditionalPropertyTypeOf<
  T extends CodeTableNumber | InternalCodeTableNumber
> = CodeTableAdditionalPropertyTypeOfType<T>;
export type CodeTableCodeTypeOf<
  T extends CodeTableNumber | InternalCodeTableNumber
> = CodeTableCodeTypeofType<T>;
export type MetadataCodeTypeOf<T extends CodeTableNumber> =
  MetadataCodeTypeOfType<T>;
export type MetadataCodeWithAdditionalPropertiesDictionary<
  T extends CodeTableNumber
> = MetadataCodeWithAdditionalPropertiesDictionaryType<T>;

export type MetadataCodeRecordWithAdditionalPropertiesDictionary<
  T extends CodeTableNumber
> = MetadataCodeRecordWithAdditionalPropertiesDictionaryType<T>;
